import { adminResourcesLanguageItalian } from '../resources/adminResources';

import type { CallflowTranslationMessages } from './callflowTranslationMessages';

const matchExitCauses = {
  continue_on_max_waiters: 'Continua fino al numero massimo di operatori in attesa',
  skip_empty: 'Salta code vuote',
  answered: 'Chiamata risposta',
  exit_key: 'Tasto di uscita',
  hangup_on_max_waiters: 'Chiudi al numero massimo di operatori in attesa',
  timeout: 'Timeout',
  ...adminResourcesLanguageItalian.call_sessions.fields.hangupCauses,
};

export const callflowLanguageItalian: CallflowTranslationMessages = {
  callflows: {
    interface: {
      toggleListVisibility: 'Attiva/disattiva la lista',
      toggleMiniMap: 'Attiva/disattiva la mappa',
      noRecordSelected: 'Nessun record selezionato',
      selectRecord: 'Seleziona record',
      selectedRecord: 'Record selezionato',
      insertElement: 'Inserisci elemento',
      rules: 'Regole',
      services: 'Servizi',
      destinations: 'Destinazioni',
      selectionModeEnabled: 'Modalità selezione attivata',
      panningModeEnabled: 'Modalità panoramica attivata',
      copyNode: 'Copia nodo',
      deleteSelected: 'Elimina selezionato',
      editAndSave: 'Modifica e salva',
      editAndSaveDescription: 'Modifica e salva le impostazioni del dialplan.',
      description: 'Descrizione',
      backToMainList: 'Torna alla lista principale',
      showBackups: 'Mostra i backup',
      backupsForTitle: 'Backup per %{title}',
      backupData: 'Backup dati',
      restoreBackup: 'Ripristina backup',
      saveAsFile: 'Salva come file',
      uploadFile: 'Carica file',
      importExport: 'Importa/Esporta',
      importExportDescription: 'Utilizzare questa interfaccia per esportare/importare il dialplan.',
      controlsTitle: '%{node} controlli',
      zoomIn: 'Zoom avanti',
      zoomOut: 'Zoom indietro',
      fitView: 'Adatta la vista',
      autoPositionNodes: 'Posiziona automaticamente i nodi',
      toggleInteractivity: "Attiva/Disattiva l'interattività",
      unsavedExit: 'Hai modifiche non salvate. Sei sicuro di voler uscire?',
      filters: {
        backupFilter: 'Filtrare i backup',
        showAll: 'Mostra tutto',
        showOnlyBackups: 'Mostra solo i backup',
        hideBackups: 'Nascondi i backup',
      },
      draft: {
        dialogTitle: 'Ci sono modifiche non salvate',
        dialogText: "Le modifiche non salvate sono più recenti dell'ultima versione di Callflow. Volete applicarle?",
        decline: 'Annulla',
        apply: 'Salva',
      },
    },
    components: {
      callPropertyInput: {
        options: {
          called_id_number: 'Numero ID Chiamato',
          called_id_name: 'Nome ID Chiamato',
          caller_id_number: 'Numero ID Chiamante',
          caller_id_name: 'Nome ID Chiamante',
          remote_network_addr: 'Indirizzo Rete Remota',
        },
      },
      matchReplaceTestInput: {
        testSubmit: 'Test',
        testMatchLabel: 'Test delle regole di match',
        testMatchReplaceLabel: 'Test match/Sostituzione',
        replacedWith: 'Sostituito con %{replacement}',
        testError: 'Errore di validazione: %{error}',
      },
      sortableInstructionsList: {
        instructions: 'Elenco di istruzioni',
        description: 'Descrizione',
        matchExitCauses: 'Criteri di uscita',
        defaultConnection: 'Connessione predefinita',
        presetsLabel: 'Preimpostazioni',
        presets: {
          busy: 'Inattivo',
        },
        match_exit_causes: matchExitCauses,
      },
    },
    nodes: {
      answer: {
        name: 'Risposta',
        delay: 'Ritardo',
        delayDescription:
          'Millisecondi da attendere prima di procedere, utile per dare tempo al percorso audio di stabilizzarsi',
        answerShouldHaveConnection: 'Il nodo di risposta dovrebbe avere una connessione',
      },
      calendar: {
        name: 'Calendario',
        calendarIsNotSelected: 'Il calendario non è stato selezionato',
        calendarHasNoTarget: 'Il calendario non ha un obiettivo',
      },
      call_me: {
        name: 'Chiamami',
      },
      conference: {
        name: 'Conferenza',
        conferenceErrorEmpty: 'Il nome della conferenza non può essere vuoto',
      },
      disconnect: {
        name: 'Disconnetti',
        reason: 'Motivo',
        reasonNormal: 'Normale',
        reasonNotFound: 'Non trovato',
      },
      distribution_list: {
        name: 'Lista di distribuzione',
        distributionListErrorEmpty: 'La lista di distribuzione non può essere vuota',
        distributionListHasNoTarget: 'La lista di distribuzione non ha un obiettivo',
      },
      extension: {
        name: 'Estensione',
        connectionShouldHaveMatchExitCause:
          'La connessione dell estensione dovrebbe avere una cause di uscita corrispondente',
        connectionShouldHaveTarget: 'La connessione dell estensione dovrebbe avere un obiettivo',
      },
      forward: {
        name: 'Inoltra',
        number: 'Numero',
        forwardErrorEmpty: 'Il numero di inoltro non è impostato!',
      },
      goto: {
        name: 'Goto',
        gotoErrorEmpty: 'La destinazione Goto non è impostata!',
      },
      groupNode: {
        name: 'Gruppo',
        groupErrorEmpty: 'Il nome del gruppo non può essere vuoto',
        callTimeout: 'Timeout della chiamata',
      },
      http: {
        name: 'HTTP',
        urlDescription: "L'URL esterno da chiamare",
        urlErrorEmpty: "L'URL non può essere vuoto",
        urlErrorInvalid: "L'URL deve essere valido e iniziare con http:// o https://",
        method: 'Metodo',
        methodDescription: 'Metodo HTTP da utilizzare per la richiesta',
        methodPost: 'POST',
        methodGet: 'GET',
        headers: 'Headers',
        headersTooltip: `Una richiesta HTTP esterna che deve restituire un altro Callflow. Quando l'esecuzione del callflow raggiunge un nodo CallflowHTTP,
        verrà effettuata una richiesta all'URL specificato; se il metodo è POST, lo stato della chiamata verrà inviato come richiesta
        payload. Il server contattato deve rispondere con un callflow, sotto forma di un oggetto JSON contenente l'ingresso
        e i campi dei nodi. Il callflow restituito non deve contenere loop, ovvero non può essere un grafo arbitrario ma
        deve essere un albero; inoltre, non tutti i nodi sono supportati, ma solo quelli con il
        x-voismart-simple-callflow-node proprietà. Se una di queste condizioni richieste per il Callflow restituito è
        violato, la chiamata verrà interrotta.`,
        addHeader: 'Aggiungi header',
        headerName: 'Nome header',
        headerNameErrorEmpty: "Il nome dell'header non può essere vuoto",
        headerNameErrorInvalid: 'Il nome deve essere valido e iniziare con x- o essere "autorization"',
        headerValue: 'Valore header',
        sslVerify: 'Verifica SSL',
      },
      hunt: {
        name: 'Hunting',
        enableScreening: 'Abilita screening',
        huntingGroup: 'Gruppo di hunting',
        parallel: 'Parallelo',
        huntingGroupErrorEmpty: 'Il gruppo di hunting non può essere vuoto',
      },
      is_from_queue: {
        name: 'Chiamata da coda?',
        isFromQueueHasNoTarget: '"Chiamata da coda?" non è connesso',
      },
      is_external: {
        name: 'Chiamata esterna?',
        isExternalHasNoTarget: '"Chiamata esterna?" non è connesso',
      },
      ivr: {
        name: 'IVR',
        errorSoundfile: 'File audio di errore',
        menuSoundfile: 'File audio del menu',
        retries: 'Ritenta',
        selections: 'Selezioni',
        selection: 'Selezione',
        timeout: 'Timeout',
        timeoutSoundfile: 'File audio del timeout',
        selectionsErrorEmpty: 'Le selezioni non possono essere vuote',
        selectionShouldHaveConnection: 'Le selezioni IVR devono avere una connessione',
        soundfileErrorEmpty: 'Il file audio non può essere vuoto',
      },
      lcr: {
        name: 'LCR',
        userIsNotSelected: "L'utente non è stato selezionato",
      },
      mapping: {
        name: 'Mappatura',
        setProperty: 'Imposta proprietà',
        customSetProperty: 'Imposta proprietà personalizzata',
        useCustomSetProperty: 'Usa proprietà personalizzata',
        addMappingRule: 'Aggiungi regola di match',
        replaceRule: 'Sostituisci regola',
        mappingRuleErrorEmpty: 'La regola di match non può essere vuota',
        mappingHasNoTarget: 'La mappatura non ha un destinatario',
        mapTo: '%{matchRule} a %{replaceRule}',
      },
      playback: {
        name: 'Riproduzione',
        audioSourceNotSelected: 'Sorgente audio non selezionata',
        useUrlSource: 'Usa sorgente URL',
        useDTMFGathering: 'Usa raccolta DTMF',
        maxDigits: 'Massimo numero di cifre',
        maxDigitsErrorInvalid: 'Il numero massimo di cifre deve essere un numero compreso tra 1 e 128',
        outputPropName: 'Nome proprietà di output',
        outputPropNameErrorEmpty: 'Il nome della proprietà in output non può essere vuoto',
        outputPropNameErrorInvalid: 'Il nome della proprietà in output deve essere valido e iniziare con una lettera',
        soundfile: 'File audio',
        terminators: 'Terminatori',
        terminatorsErrorInvalid: 'I terminatori devono essere validi e contenere solo cifre, # e *.',
        timeout: 'Timeout',
        timeoutErrorInvalid: 'Il timeout deve essere un numero compreso tra 500 e 60000.',
      },
      presence_router: {
        name: 'Router di presenza',
        availableConnection: 'Connessione disponibile',
        awayConnection: 'Connessione in assente',
        presenceRouterShouldHaveConnection: 'Il router di presenza dovrebbe avere una connessione',
      },
      queue: {
        name: 'Coda',
        queueErrorEmpty: 'Il nome della coda non può essere vuoto',
        connectionShouldHaveMatchExitCause:
          'La connessione della coda dovrebbe avere una cause di uscita corrispondente',
        connectionShouldHaveTarget: 'La connessione della coda dovrebbe avere una coda',
      },
      response_router: {
        name: 'Router di risposta',
        busyConnection: 'Connessione in conflitto',
        timeoutConnection: 'Connessione in timeout',
        unreachableConnection: 'Connessione non raggiungibile',
        responseRouterShouldHaveConnection: 'Il router di risposta dovrebbe avere una connessione',
      },
      router: {
        name: 'Router',
        matchProperty: 'Proprietà per il match',
        customMatchProperty: 'Proprietà di per il match personalizzata',
        useCustomMatchProperty: 'Usa proprietà per il match personalizzata',
        callPropertyErrorEmpty: '%{label} non può essere vuoto',
        callPropertyErrorInvalid:
          '%{label} deve essere composta da almeno 3 simboli, iniziare con una lettera e contenere solo lettere e numeri',
        matchRules: 'Regole per il match',
        matchRule: 'Regola per il match',
        matchRuleErrorEmpty: 'La regola di match non può essere vuota',
        matchRuleShouldHaveConnection: 'La regola di match del router deve avere una connessione',
      },
      speech: {
        name: 'Voce',
        auth_key: 'Chiave di autorizzazione',
        authKeyErrorEmpty: 'La chiave di autorizzazione non può essere vuota',
        encoding: 'Codifica',
        encodingWav: 'WAV',
        encodingMp3: 'MP3',
        language: 'Lingua',
        languageIt: 'Italiano',
        languageEn: 'Inglese',
        text: 'Testo',
        textErrorEmpty: 'Il testo non può essere vuoto',
      },
      user: {
        name: 'Utente',
        userIsNotSelected: 'Utente non selezionato',
        connectionShouldHaveMatchExitCause: 'L utente dovrebbe avere una cause di uscita corrispondente',
        connectionShouldHaveTarget: 'L utente dovrebbe avere una coda',
      },
      personal_voicemail: {
        name: 'Segreteria',
      },
      user_voicemail: {
        name: 'Segreteria',
        userIsNotSelected: 'Utente non selezionato',
      },
      ai_assistant: {
        name: 'Assistente AI',
        instructions: 'Istruzioni',
        instructionsErrorEmpty: 'Le istruzioni non possono essere vuote',
        model: 'Modello',
        voice: 'Voce',
        defaultInstructions:
          "Sei il centralinista di un'azienda. Dai il benvenuto al chiamante e chiedi come puoi aiutarlo. Utilizza i tuoi strumenti per inoltrare la chiamata alla destinazione più appropriata secondo la richiesta del chiamante. Utilizza un tono educato e professionale. Mantieni la conversazione inerente all'azienda e non consentire al chiamante di deviarla su altri argomenti.",
        transcription: {
          enabled: 'Abilita trascrizione',
          model: 'Modello di trascrizione',
        },
      },
      label: 'Etichetta',
    },
    notifications: {
      saved: 'Dialplan salvato con successo',
      updated: 'Dialplan aggiornato con successo',
      restored: 'Il backup di Dialplan è stato ripristinato con successo',
      errorSaving: 'Errore durante il salvataggio del dialplan. Controlla il tuo dialplan e riprova.',
      callflowDataError: 'I dati del flusso di chiamata non sono validi: %{error}',
      deleteConfirmation: 'Sei sicuro di voler eliminare questo dialplan?',
      deleteConfirmationTitle: 'Elimina %{title}?',
      nodeIsNotConnectedToMainFlow: 'Il nodo non è connesso al flusso principale',
      errorSorting:
        "Errore nell'ordinamento dei nodi. Verificare che i nodi siano collegati correttamente e riprovare.",
      errorSortingMultipleRoots:
        "Errore nell'ordinamento dei nodi. Non tutti i nodi sono collegati al flusso principale.",
    },
  },
};
